import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { from, Observable, switchMap } from 'rxjs';
import { AuthService } from '@ee/common/services';
import { AUTHORIZATION_BEARER_TOKEN_PREFIX } from '@ee/common/constants';
import { environment } from '../../../environments/environment';

/**
 * Attaches the Cognito bearer token to authenticated /api/help calls. Public help endpoints
 * (/api/help/public/**) and any non-API request pass through untouched, so anonymous browsing works
 * without a session. Unlike the portal interceptor, a missing token does NOT force a logout here —
 * the help center is usable while signed out.
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private static readonly PUBLIC_API_PATHS = ['api/help/public'];

  constructor(private authService: AuthService) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!request.url.startsWith(environment.api_prefix) || this.isPublicEndpoint(request.url)) {
      return next.handle(request);
    }

    return from(this.authService.getAccessToken().catch(() => null)).pipe(
      switchMap((token) => {
        if (token && !request.headers.has('Authorization')) {
          return next.handle(
            request.clone({
              setHeaders: { Authorization: `${AUTHORIZATION_BEARER_TOKEN_PREFIX}${token}` },
            })
          );
        }
        return next.handle(request);
      })
    );
  }

  private isPublicEndpoint(url: string): boolean {
    return AuthInterceptor.PUBLIC_API_PATHS.some((path) => url.includes(path));
  }
}
